import {
  TextMemoElement,
  GraphBuilderElement,
  CrosstabElement,
  StatisticsSummaryElement
} from '../../types/report.types';
import { DataSet, DatasetId, ColumnId, ColumnName, createReportElementId, createDatasetId } from '../../types';
import { HistogramConfig, ScatterPlotConfig, TimeSeriesConfig } from '../../types/graph-builder.types';
import type { CrosstabConfiguration } from './crosstab/types';

/**
 * レポート要素を作成するためのファクトリ関数群
 * 各要素タイプに対応した作成関数を提供
 */

/**
 * GraphBuilder要素を作成するヘルパー関数
 */
export function createGraphBuilderElement(
  graphConfig: HistogramConfig | ScatterPlotConfig | TimeSeriesConfig,
  dataset: DataSet,
  title?: string
): GraphBuilderElement {
  const now = new Date().toISOString();

  return {
    id: createReportElementId(`graph-${crypto.randomUUID()}`),
    type: 'graph_builder',
    datasetId: dataset.id,
    graphConfig,
    title: title || '',
    height: 400,
    marginBottom: 16,
    createdAt: now,
    lastModified: now
  };
}

/**
 * ヒストグラム要素を作成（GraphBuilderを使用）
 */
export function createHistogramElement(
  columnId: ColumnId,
  dataset: DataSet,
  bins: number = 10
): GraphBuilderElement {
  const histogramConfig: HistogramConfig = {
    type: 'histogram',
    datasetId: dataset.id,
    column: columnId,
    bins,
    showDensity: false,
    orientation: 'vertical'
  };
  
  // 列名を取得してタイトルに使用
  const column = dataset.schema.columns.find(col => col.id === columnId);
  const columnName = column?.name || columnId;
  
  return createGraphBuilderElement(
    histogramConfig,
    dataset,
    `Histogram: ${columnName}`
  );
}

/**
 * 散布図要素を作成（GraphBuilderを使用）
 */
export function createScatterPlotElement(
  xColumn: ColumnId,
  yColumn: ColumnId,
  dataset: DataSet
): GraphBuilderElement {
  const scatterConfig: ScatterPlotConfig = {
    type: 'scatter',
    datasetId: dataset.id,
    xColumn,
    yColumn
  };
  
  // 列名を取得してタイトルに使用
  const xCol = dataset.schema.columns.find(col => col.id === xColumn);
  const yCol = dataset.schema.columns.find(col => col.id === yColumn);
  const xName = xCol?.name || xColumn;
  const yName = yCol?.name || yColumn;
  
  return createGraphBuilderElement(
    scatterConfig,
    dataset,
    `${yName} vs ${xName}`
  );
}

/**
 * 時系列プロット要素を作成（GraphBuilderを使用）
 */
export function createTimeSeriesPlotElement(
  dateColumn: ColumnId,
  valueColumn: ColumnId,
  dataset: DataSet
): GraphBuilderElement {
  const timeSeriesConfig: TimeSeriesConfig = {
    type: 'timeseries',
    datasetId: dataset.id,
    xColumn: dateColumn,
    yColumns: [valueColumn]
  };
  
  // 列名を取得してタイトルに使用
  const valueCol = dataset.schema.columns.find(col => col.id === valueColumn);
  const valueName = valueCol?.name || valueColumn;
  
  return createGraphBuilderElement(
    timeSeriesConfig,
    dataset,
    `Time Series: ${valueName}`
  );
}

/**
 * マルチ系列時系列プロット要素を作成（GraphBuilderを使用）
 */
export function createMultiTimeSeriesPlotElement(
  dateColumn: ColumnId,
  valueColumns: ColumnId[],
  dataset: DataSet
): GraphBuilderElement {
  const timeSeriesConfig: TimeSeriesConfig = {
    type: 'timeseries',
    datasetId: dataset.id,
    xColumn: dateColumn,
    yColumns: valueColumns
  };
  
  // 列名を取得してタイトルに使用
  const valueNames = valueColumns.map(colId => {
    const col = dataset.schema.columns.find(c => c.id === colId);
    return col?.name || colId;
  });
  
  return createGraphBuilderElement(
    timeSeriesConfig,
    dataset,
    `Multi-Series: ${valueNames.join(', ')}`
  );
}

/**
 * テキスト要素を作成
 */
export function createTextElement(
  content: string = '',
  height: number = 120
): TextMemoElement {
  const now = new Date().toISOString();
  
  return {
    id: createReportElementId(`text-${crypto.randomUUID()}`),
    type: 'text_memo',
    content,
    height,
    marginBottom: 16,
    createdAt: now,
    lastModified: now,
    formatting: {
      fontSize: 14,
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#1f2937',
      backgroundColor: '#f9fafb',
      textAlign: 'left',
      bold: false,
      italic: false
    }
  };
}

/**
 * Crosstab（クロス集計）要素を作成
 */
export function createCrosstabElement(
  crosstabConfiguration: CrosstabConfiguration,
  dataset: DataSet,
  title?: string
): CrosstabElement {
  const now = new Date().toISOString();

  return {
    id: createReportElementId(`crosstab-${crypto.randomUUID()}`),
    type: 'crosstab',
    crosstabConfiguration,
    datasetId: dataset.id,
    title: title || `Crosstab - ${dataset.name}`,
    height: 400,
    marginBottom: 16,
    createdAt: now,
    lastModified: now
  };
}

/**
 * StatisticsSummary（統計サマリー）要素を作成
 */
export function createStatisticsSummaryElement(
  targetColumns: ColumnName[],
  datasetId: string | DatasetId
): StatisticsSummaryElement {
  const now = new Date().toISOString();

  return {
    id: createReportElementId(`stats-${crypto.randomUUID()}`),
    type: 'statistics_summary',
    targetColumns,
    datasetId: typeof datasetId === 'string' ? createDatasetId(datasetId) : datasetId,
    displayFormat: 'cards',
    height: 300,
    marginBottom: 16,
    createdAt: now,
    lastModified: now
  };
}